import { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import { GitHubInstallButton } from "./GitHubInstallButton";

interface GitHubRepo {
  id: string;
  installationId: number;
  repoId: number;
  repoFullName: string;
  repoName: string;
  ownerLogin: string;
  isPrivate: boolean;
  defaultBranch: string;
}

interface RepoSelectorProps {
  value?: string;
  onChange: (repo: GitHubRepo | null) => void;
  onBranchChange?: (branch: string) => void;
  selectedBranch?: string;
  showBranchSelector?: boolean;
}

export function RepoSelector({
  value,
  onChange,
  onBranchChange,
  selectedBranch,
  showBranchSelector = true,
}: RepoSelectorProps) {
  const [repos, setRepos] = useState<GitHubRepo[]>([]);
  const [branches, setBranches] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch repos on mount
  useEffect(() => {
    fetchRepos();
  }, []);

  // Fetch branches when repo changes
  useEffect(() => {
    if (value && showBranchSelector) {
      fetchBranches(value);
    } else {
      setBranches([]);
    }
  }, [value, showBranchSelector]);

  const fetchRepos = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch("/api/github/repos");
      if (!response.ok) {
        throw new Error("Failed to fetch repositories");
      }
      const data = await response.json();
      setRepos(data.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load repos");
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async (repoFullName: string) => {
    try {
      setLoadingBranches(true);
      const [owner, repo] = repoFullName.split("/");
      const response = await fetch(`/api/github/repos/${owner}/${repo}/branches`);
      if (!response.ok) {
        console.warn("Failed to fetch branches");
        setBranches([]);
        return;
      }
      const data = await response.json();
      setBranches(data.data || []);
    } catch (err) {
      console.warn("Failed to fetch branches:", err);
      setBranches([]);
    } finally {
      setLoadingBranches(false);
    }
  };

  const handleRefresh = async () => {
    // Get unique installation IDs from repos
    const installationIds = [...new Set(repos.map((r) => r.installationId))];

    setRefreshing(true);
    try {
      // Refresh each installation
      await Promise.all(
        installationIds.map((id) =>
          fetch(`/api/github/installations/${id}/refresh`, { method: "POST" })
        )
      );
      // Refetch repos
      await fetchRepos();
    } catch (err) {
      console.error("Failed to refresh:", err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleRepoChange = (repoFullName: string) => {
    if (repoFullName === "__none__") {
      onChange(null);
      return;
    }
    const repo = repos.find((r) => r.repoFullName === repoFullName);
    if (repo) {
      onChange(repo);
      // Set default branch
      if (onBranchChange && repo.defaultBranch) {
        onBranchChange(repo.defaultBranch);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <LoadingSpinner />
        Loading repositories...
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive">{error}</p>
        <Button variant="outline" size="sm" onClick={fetchRepos}>
          Retry
        </Button>
      </div>
    );
  }

  if (repos.length === 0) {
    return (
      <div className="space-y-3 p-4 border rounded-lg bg-muted/50">
        <p className="text-sm text-muted-foreground">
          No repositories found. Connect your GitHub account to select a repository.
        </p>
        <GitHubInstallButton variant="outline" size="sm" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Select value={value || "__none__"} onValueChange={handleRepoChange}>
          <SelectTrigger className="flex-1">
            <SelectValue placeholder="Select a repository" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">No repository selected</SelectItem>
            {repos.map((repo) => (
              <SelectItem key={repo.repoFullName} value={repo.repoFullName}>
                <span className="flex items-center gap-2">
                  {repo.repoFullName}
                  {repo.isPrivate && (
                    <LockIcon className="h-3 w-3 text-muted-foreground" />
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="icon"
          onClick={handleRefresh}
          disabled={refreshing}
          title="Refresh repositories"
        >
          <RefreshIcon className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {showBranchSelector && value && (
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium min-w-[60px]">Branch:</label>
          {loadingBranches ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <LoadingSpinner />
              Loading...
            </div>
          ) : branches.length > 0 ? (
            <Select
              value={selectedBranch || ""}
              onValueChange={(b) => onBranchChange?.(b)}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select a branch" />
              </SelectTrigger>
              <SelectContent>
                {branches.map((branch) => (
                  <SelectItem key={branch} value={branch}>
                    {branch}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <input
              type="text"
              className="flex-1 h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              placeholder="main"
              value={selectedBranch || ""}
              onChange={(e) => onBranchChange?.(e.target.value)}
            />
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        <GitHubInstallButton variant="outline" size="sm" />
        <span className="text-xs text-muted-foreground">
          Add more repositories
        </span>
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <svg
      className="animate-spin h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 2v6h-6" />
      <path d="M3 12a9 9 0 0115-6.7L21 8" />
      <path d="M3 22v-6h6" />
      <path d="M21 12a9 9 0 01-15 6.7L3 16" />
    </svg>
  );
}
